import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Button, Callout, copyToClipboard, type ActionMenuItem, type IconName } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import {
  LANDING_PATH_MAX,
  LANDING_PATH_MIN,
  cleanSlugInput,
  landingPathProblem,
  landingUrl,
  type LandingHost,
  type LandingPublication,
} from '../../landing-page-api';
import { formatVersionTimestamp } from '../../storyboard';
import { BrowserBar, type BarStatus } from './BrowserBar';
import * as S from './LandingPageComposer.styles';

/** What a publish or unpublish answered, for the controls to show. */
export type PublishFailure = { kind: 'path_taken' } | { kind: 'error'; text: string };

/** The tracking links landing on the page (its own link included), for the unpublish confirmation: being read (the
 *  confirmation waits for them), counted, or not known (the read failed: the confirmation does not wait). */
export type LinksHere = { status: 'checking' } | { status: 'known'; count: number } | { status: 'unknown' };

export interface PublishControlsProps {
  publication?: LandingPublication;
  /** The landing host page URLs are built with; undefined when the brand has none (or before it is known). */
  host?: LandingHost;
  /** The landing hosts have been read (so a missing host means the brand has none). */
  hostsKnown: boolean;
  /** The landing hosts could not be read. */
  hostsFailed: boolean;
  /** The paths other live pages of the brand use (this page's own excluded): taken. */
  takenPaths: readonly string[];
  /** The path a never-published page is suggested (from what it promotes); editable like a typed one. */
  defaultSlug: string;
  /** A publish or unpublish is on its way. */
  busy: 'publish' | 'unpublish' | null;
  failure: PublishFailure | null;
  /** Why nothing can be published right now (translated), or null. */
  blockedText: string | null;
  /** The version on screen is the live one. */
  selectedIsLive: boolean;
  /** The version on screen is generated (locked), so it is exactly what went live. */
  selectedLocked: boolean;
  /** The version on screen has no hero image (neither the owner's nor a generated one). */
  noHero: boolean;
  onPublish: (path: string) => void;
  onUnpublish: () => void;
  /** The owner edited the path (a refusal of the previous one no longer applies). */
  onPathEdit?: () => void;
  onNotify: (message: string) => void;
  /** The tracking links landing on the page: unpublishing sends their visitors to the casino's home page without
   *  their query, so their tracking is lost, and the confirmation says so. */
  linksHere?: LinksHere;
  /** The unpublish confirmation opened: read the links landing on the page again. */
  onUnpublishAsk?: () => void;
}

export interface PublishControlsView {
  /** The mock browser bar over the preview: the page's address with its path editable, a copy button, its state. */
  addressBar: React.ReactElement;
  /** The preview head's right side: the publish pill, the more menu and the confirmation. */
  actions: React.ReactElement;
  /** What publishing has to say (a path problem, a failure, no landing domain, a busy note), or null. */
  notice: React.ReactElement | null;
}

type Confirm = 'publish' | 'unpublish' | null;

const slugOf = (path: string): string => path.replace(/^\/+/, '');

/**
 * The composer's publishing, spread over the preview: the browser bar's address (`https://lp.<domain>/<path>`, its
 * path edited in place and checked as it is typed: lowercase letters, digits, `-` and `_`, 2 to 60 of them, not a
 * path another live page of the brand uses) and state; a pill that publishes, updates the live page or retries, its
 * label following the publication (Published, and inert, when the generated version on screen is the live one at
 * its path); and a more menu with the rest (copy or open the live page, Republish, Unpublish). Publishing and
 * unpublishing are confirmed first; the unpublish confirmation counts the tracking links landing on the page (their
 * tracking is lost while it is offline) and waits for that count. A brand with no landing domain is told why it
 * cannot publish.
 */
export const usePublishControls = ({
  publication,
  host,
  hostsKnown,
  hostsFailed,
  takenPaths,
  defaultSlug,
  busy,
  failure,
  blockedText,
  selectedIsLive,
  selectedLocked,
  noHero,
  onPublish,
  onUnpublish,
  onPathEdit,
  onNotify,
  linksHere,
  onUnpublishAsk,
}: PublishControlsProps): PublishControlsView => {
  const { t } = useTranslation();
  const problemId = React.useId();
  const isLive = publication?.state === 'live';
  const livePath = publication?.path ?? '';
  // The owner's edit of the path, kept for the publication path it was made over: a publish that moves the page
  // drops it (the bar then shows the path the page went live at).
  const [slugEdit, setSlugEdit] = React.useState<{ over: string; value: string } | null>(null);
  const edited = slugEdit && slugEdit.over === livePath ? slugEdit.value : null;
  const slug = edited ?? (livePath ? slugOf(livePath) : defaultSlug);
  const path = `/${slug}`;
  const problem = landingPathProblem(slug);
  const ownPath = livePath === path;
  const taken = !ownPath && takenPaths.includes(path);
  const lpDomain = host?.lp_domain || publication?.lp_domain || '';
  const previewUrl = lpDomain && !problem ? landingUrl(lpDomain, path) : '';
  const liveUrl = isLive ? publication?.url ?? '' : '';

  const [confirm, setConfirm] = React.useState<Confirm>(null);
  const { closeHandle, dismiss } = useSheetDismiss(() => setConfirm(null));

  const problemText =
    problem === 'empty'
      ? t('Choose the path the page lives at.')
      : problem === 'chars'
        ? t('Use lowercase letters, digits, hyphens and underscores only.')
        : problem === 'short'
          ? t('Use at least {{n}} characters.', { n: LANDING_PATH_MIN })
          : problem === 'long'
            ? t('Use at most {{n}} characters.', { n: LANDING_PATH_MAX })
            : taken || failure?.kind === 'path_taken'
              ? t('Another live page of your brand already uses this path.')
              : null;

  const noDomain = hostsKnown && !host && !publication?.lp_domain;
  // The generated version on screen is the live one at the path in the bar: nothing to publish. A live draft can have
  // changed since it went live, and a moved path is a real update, so both keep the pill.
  const published = isLive && selectedIsLive && selectedLocked && ownPath;
  const canPublish = !busy && !blockedText && !problem && !taken && Boolean(lpDomain) && !hostsFailed && !published;

  const notifyCopy = (ok: boolean, done: string): void => onNotify(ok ? done : t("Couldn't copy to clipboard"));

  /* ── The browser bar ── */

  const status: BarStatus = !publication
    ? 'none'
    : publication.state === 'live'
      ? 'live'
      : publication.state === 'failed'
        ? 'failed'
        : 'unpublished';
  const addressBar = (
    <BrowserBar
      host={lpDomain ? `${lpDomain}/` : '/'}
      slug={slug}
      onSlugChange={(value) => {
        setSlugEdit({ over: livePath, value: cleanSlugInput(value) });
        onPathEdit?.();
      }}
      disabled={busy !== null}
      invalid={Boolean(problemText)}
      problemId={problemId}
      url={previewUrl}
      onCopied={() => onNotify(t('Address copied'))}
      onCopyFailed={() => onNotify(t("Couldn't copy to clipboard"))}
      status={status}
    />
  );

  /* ── The pill and the more menu ── */

  const pillLabel =
    busy === 'publish'
      ? t('Publishing…')
      : busy === 'unpublish'
        ? t('Unpublishing…')
        : published
          ? t('Published')
          : isLive
            ? t('Update live page')
            : publication?.state === 'failed'
              ? t('Retry')
              : publication
                ? t('Publish again')
                : t('Publish');
  const pillIcon: IconName = published ? 'check' : isLive || publication?.state === 'failed' ? 'refresh' : 'globe';
  // Why the pill is inert, or what it would change (its tooltip).
  const pillHint =
    blockedText ??
    (published
      ? t('This version is the live one.')
      : isLive && publication?.version_at && !selectedIsLive
        ? t('The live version is from {{when}}. Update the live page to publish the one on screen.', {
            when: formatVersionTimestamp(Date.parse(publication.version_at)),
          })
        : undefined);

  const menu: ActionMenuItem[] = [];
  // The bar copies the address it shows; the live one differs only while the owner edits the path.
  if (liveUrl && liveUrl !== previewUrl) {
    menu.push({
      label: t('Copy live link'),
      icon: 'copy',
      onSelect: () => void copyToClipboard(liveUrl).then((ok) => notifyCopy(ok, t('Address copied'))),
    });
  }
  if (liveUrl) {
    menu.push({
      label: t('Open live page'),
      icon: 'external-link',
      onSelect: () => {
        window.open(liveUrl, '_blank', 'noopener,noreferrer');
      },
    });
  }
  // The live version on screen: render it again and update the live page in place (same page id and public id, no
  // downtime), e.g. to pick up template fixes. The way to get them without unpublishing and publishing again.
  if (published) {
    menu.push({
      label: busy === 'publish' ? t('Republishing…') : t('Republish'),
      icon: 'refresh',
      disabled: busy !== null,
      onSelect: () => onPublish(livePath),
    });
  }
  if (isLive) {
    menu.push({
      label: t('Unpublish'),
      icon: 'close-circle',
      destructive: true,
      disabled: busy !== null,
      onSelect: () => {
        setConfirm('unpublish');
        onUnpublishAsk?.();
      },
    });
  }

  // Unpublishing waits for the links landing here to be counted (a failed read does not hold it up).
  const checkingLinks = confirm === 'unpublish' && linksHere?.status === 'checking';
  const linksCount = linksHere?.status === 'known' ? linksHere.count : 0;

  const actions = (
    <S.PublishActions>
      <S.PillWrap title={pillHint}>
        <Button
          variant="primary"
          icon={pillIcon}
          label={pillLabel}
          disabled={!canPublish}
          onClick={() => {
            if (canPublish) setConfirm('publish');
          }}
        />
      </S.PillWrap>
      {menu.length > 0 ? <ActionMenu items={menu} align="end" disabled={busy !== null} /> : null}

      {confirm ? (
        <Sheet
          title={
            confirm === 'unpublish'
              ? t('Unpublish this landing page?')
              : isLive
                ? t('Update the live page?')
                : t('Publish this landing page?')
          }
          onClose={() => setConfirm(null)}
          closeHandle={closeHandle}
          maxWidth="460px"
          footer={
            <S.ConfirmFoot>
              <Button variant="text" label={t('Cancel')} onClick={dismiss} />
              <Button
                variant="primary"
                label={confirm === 'unpublish' ? t('Unpublish') : isLive ? t('Update live page') : t('Publish')}
                disabled={checkingLinks}
                onClick={() => {
                  if (checkingLinks) return;
                  const what = confirm;
                  dismiss();
                  if (what === 'unpublish') onUnpublish();
                  else onPublish(path);
                }}
              />
            </S.ConfirmFoot>
          }
        >
          <S.Stack>
            {confirm === 'unpublish' ? (
              <S.ConfirmBody
                text="{{url}} goes offline: until you publish it again, visitors to it are sent to your casino's home page. Tracking links to this address keep working once you republish."
                placeholders={{ url: publication?.url || path }}
              />
            ) : publication?.state === 'unpublished' && publication.url === previewUrl ? (
              <S.ConfirmBody
                text="The version on screen goes live again at {{url}} for every visitor at once. Tracking links to this address work again."
                placeholders={{ url: previewUrl }}
              />
            ) : isLive ? (
              <S.ConfirmBody
                text="The version on screen replaces the live page at {{url}} for every visitor at once."
                placeholders={{ url: previewUrl || path }}
              />
            ) : (
              <S.ConfirmBody
                text="The version on screen goes live at {{url}} for every visitor at once."
                placeholders={{ url: previewUrl || path }}
              />
            )}
            {confirm === 'unpublish' && checkingLinks ? (
              <S.PreviewStatus aria-live="polite">
                <S.Spinner aria-hidden="true" />
                <S.NoteText text="Checking the tracking links that send visitors here…" />
              </S.PreviewStatus>
            ) : null}
            {confirm === 'unpublish' && !checkingLinks && linksCount > 0 ? (
              <Callout
                tone="warning"
                text={
                  linksCount === 1
                    ? t(
                        "1 tracking link sends visitors here. Unpublishing sends them to your casino's home page and its tracking is lost.",
                      )
                    : t(
                        "{{n}} tracking links send visitors here. Unpublishing sends them to your casino's home page and their tracking is lost.",
                        { n: linksCount },
                      )
                }
              />
            ) : null}
            {confirm === 'publish' && noHero ? (
              <Callout
                tone="warning"
                text={t('This version has no hero image yet. Generate one with Lumio or use your own.')}
              />
            ) : null}
          </S.Stack>
        </Sheet>
      ) : null}
    </S.PublishActions>
  );

  /* ── What publishing has to say, under the preview head ── */

  const lines: React.ReactElement[] = [];
  if (hostsFailed && !publication) {
    lines.push(
      <Callout
        key="hosts"
        tone="warning"
        text={t("Your landing domain couldn't be read, so the page can't be published right now.")}
      />,
    );
  } else if (noDomain) {
    lines.push(
      <Callout
        key="domain"
        tone="info"
        text={t(
          'This brand has no landing domain yet, so the page cannot be published. Once your landing domain is set up, publish it from here.',
        )}
      />,
    );
  }
  if (publication?.state === 'failed' && publication.error) {
    lines.push(<Callout key="pub-error" tone="danger" text={publication.error} />);
  }
  if (failure?.kind === 'error') lines.push(<Callout key="failure" tone="danger" text={failure.text} />);
  if (problemText) {
    lines.push(
      <S.PathProblem key="path" id={problemId}>
        <S.FieldError raw text={problemText} />
      </S.PathProblem>,
    );
  }
  if (busy === 'unpublish') {
    lines.push(
      <S.PreviewStatus key="busy" aria-live="polite">
        <S.Spinner aria-hidden="true" />
        <S.NoteText text="Taking the page offline. This can take a few seconds…" />
      </S.PreviewStatus>,
    );
  } else if (busy === 'publish') {
    lines.push(
      <S.PreviewStatus key="busy" aria-live="polite">
        <S.Spinner aria-hidden="true" />
        <S.NoteText text="Publishing the page…" />
      </S.PreviewStatus>,
    );
  }
  const notice = lines.length > 0 ? <S.PublishNotice>{lines}</S.PublishNotice> : null;

  return { addressBar, actions, notice };
};
